import { useEffect, useState } from "react";
import { api } from "../../lib/api";

export default function SettingsPreferences() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [timezone, setTimezone] = useState("UTC");
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [postVariationsPerPlatform, setPostVariationsPerPlatform] = useState(1);

  useEffect(() => {
    api.auth
      .me()
      .then(({ profile }) => {
        if (profile) {
          setTimezone(profile.timezone);
          setEmailNotifications(profile.emailNotifications);
          setPostVariationsPerPlatform(profile.postVariationsPerPlatform ?? 1);
        }
      })
      .catch(() => setError("Failed to load preferences"))
      .finally(() => setLoading(false));
  }, []);

  const handleSavePreferences = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await api.me.patchProfile({
        timezone,
        emailNotifications,
        postVariationsPerPlatform,
      });
      setSuccess("Preferences saved.");
    } catch {
      setError("Failed to save preferences.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-8">
        <h1 className="page-title">Preferences</h1>
        <p className="text-sm text-muted mt-1">
          Customize your timezone, post generation settings, and notification
          preferences.
        </p>
      </div>

      {error && (
        <div className="mb-6 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-6 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
          {success}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-muted">Loading…</div>
      ) : (
        <div className="card p-8 space-y-7">
          <div>
            <label className="label">Timezone</label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="input w-full max-w-xs"
            >
              {[
                "UTC",
                "America/New_York",
                "America/Chicago",
                "America/Denver",
                "America/Los_Angeles",
                "America/Toronto",
                "Europe/London",
                "Europe/Paris",
                "Europe/Berlin",
                "Asia/Tokyo",
                "Asia/Singapore",
                "Australia/Sydney",
              ].map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted mt-1">
              Used for scheduling and calendar display.
            </p>
          </div>

          <div>
            <label className="label !mb-1">Post variations per platform</label>
            <p className="text-xs text-muted mb-3">
              How many distinct AI variations to generate for each platform in a
              batch. More variations gives you more to choose from, but uses
              more of your monthly quota.
            </p>
            <div className="flex gap-2">
              {[1, 2, 3].map((n) => (
                <button
                  key={n}
                  onClick={() => setPostVariationsPerPlatform(n)}
                  aria-pressed={postVariationsPerPlatform === n}
                  className={`w-14 h-10 rounded-lg border text-sm font-semibold transition-colors ${
                    postVariationsPerPlatform === n
                      ? "bg-brand-500 text-white border-brand-500"
                      : "border-gray-300 text-slate hover:border-brand-400"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <p className="text-xs text-faint mt-2">
              {postVariationsPerPlatform === 1
                ? "1 post per platform per batch (default)."
                : `${postVariationsPerPlatform} variations per platform — each with a different hook, angle, or tone.`}
            </p>
          </div>

          <div className="flex items-start gap-3">
            <input
              id="email-notifs"
              type="checkbox"
              checked={emailNotifications}
              onChange={(e) => setEmailNotifications(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            <label
              htmlFor="email-notifs"
              className="text-[15px] text-slate cursor-pointer"
            >
              <span className="font-bold text-ink">Email notifications</span>
              <br />
              <span className="text-muted">
                Receive emails when scheduled posts are ready for review or
                published.
              </span>
            </label>
          </div>

          <button
            onClick={handleSavePreferences}
            disabled={saving}
            className="btn-primary"
          >
            {saving ? "Saving…" : "Save preferences"}
          </button>
        </div>
      )}
    </div>
  );
}
